/**
 * Audit category presentation (#1119 Slice 3).
 *
 * One source for the human category label + badge color of an audit
 * event, shared by:
 *   - the customer Org Audit Log (`OrgSettings/Audit`), where the SERVER
 *     already projected a category key onto each row (Slice 1), and
 *   - the Global Admin Audit Log (`Admin/Audit`), which has the raw
 *     `action` key and derives the category from its resource prefix.
 *
 * The customer page only ever sees whitelisted categories; the admin
 * page sees the full catalog (incl. security / billing / system), so the
 * canonical key set here is a superset of the customer's `OrgAuditCategory`.
 */

export interface AuditCategoryMeta {
  /** Canonical category key. */
  key: string
  /** Human label for the badge. */
  label: string
  /** Tailwind classes for the badge (light + dark). */
  className: string
}

/** Canonical categories → label + color. The first six keys match the
 * server's `OrgAuditCategory` (customer-visible); the rest are admin-only. */
const CATEGORY_META: Record<string, AuditCategoryMeta> = {
  members: { key: 'members', label: 'Members', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300' },
  workspaces: { key: 'workspaces', label: 'Workspaces', className: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300' },
  teams: { key: 'teams', label: 'Teams', className: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300' },
  organization: { key: 'organization', label: 'Organization', className: 'bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300' },
  'test-data': { key: 'test-data', label: 'Test data', className: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300' },
  integrations: { key: 'integrations', label: 'Integrations', className: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300' },
  // admin-only categories
  security: { key: 'security', label: 'Security', className: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' },
  billing: { key: 'billing', label: 'Billing', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  users: { key: 'users', label: 'Users', className: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' },
  system: { key: 'system', label: 'System', className: 'bg-gray-200 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300' },
  other: { key: 'other', label: 'Other', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300' },
}

/** Resource prefix (the part before the first dot in an action key) →
 * canonical category. Covers the full `AUDIT_ACTIONS` catalog. */
const RESOURCE_TO_CATEGORY: Record<string, string> = {
  member: 'members',
  invitation: 'users',
  org: 'organization',
  workspaceMember: 'workspaces',
  workspace: 'workspaces',
  team: 'teams',
  teamMembers: 'teams',
  credential: 'test-data',
  testDataSet: 'test-data',
  dataBinding: 'test-data',
  slack: 'integrations',
  billing: 'billing',
  superadmin: 'security',
  impersonation: 'security',
  auth: 'security',
  user: 'users',
  appSettings: 'system',
  pricing: 'system',
  prompt: 'system',
  rag: 'system',
}

/** Category for a customer row, keyed by the server-projected category. */
export function categoryMetaByKey(key: string): AuditCategoryMeta {
  return CATEGORY_META[key] ?? CATEGORY_META.other
}

/** Category for an admin row, derived from the raw action key's resource
 * prefix. Unknown / new prefixes fall back to "Other" (never throws). */
export function categoryForAction(action: string): AuditCategoryMeta {
  const prefix = action.split('.')[0] ?? ''
  return categoryMetaByKey(RESOURCE_TO_CATEGORY[prefix] ?? 'other')
}
